import { FC, useMemo, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type IconName } from 'src/components/Icon';
import { Loader } from 'src/components';
import * as S from './styles';

const msgs = defineMessages({
  learned: { id: 'mywords_marker_learned' },
  heard: { id: 'mywords_marker_heard' },
  used: { id: 'mywords_marker_used' },
  wordsCount: { id: 'wordlists_viewlist_section1_subheader' },
  learnedCount: { id: 'words_detail_learned_count' },
  heardCountPart1: { id: 'words_detail_heard_count_part1' },
  heardCountPart2: { id: 'words_detail_heard_count_part2' },
  usedCountPart1: { id: 'words_detail_used_count_part1' },
  usedCountPart2: { id: 'words_detail_used_count_part2' },
});

export type MyWordsCounterType = 'learned' | 'heard' | 'used';

export interface MyWordsCounterProps {
  type: MyWordsCounterType;
  count?: number;
  occurrences?: number;
  sessions?: number;
  isLoading?: boolean;
}

const COUNTER_TYPES = {
  LEARNED: 'learned',
  HEARD: 'heard',
  USED: 'used',
} as const;

const MESSAGE_KEYS = {
  WORDS_COUNT: 'wordsCount',
  LEARNED_COUNT: 'learnedCount',
  HEARD_COUNT_PART1: 'heardCountPart1',
  HEARD_COUNT_PART2: 'heardCountPart2',
  USED_COUNT_PART1: 'usedCountPart1',
  USED_COUNT_PART2: 'usedCountPart2',
} as const;

const typeConfig: Record<MyWordsCounterType, { icon: IconName; messageKey: keyof typeof msgs }> = {
  [COUNTER_TYPES.LEARNED]: { icon: 'idea', messageKey: 'learned' },
  [COUNTER_TYPES.HEARD]: { icon: 'headphones', messageKey: 'heard' },
  [COUNTER_TYPES.USED]: { icon: 'communicateOutline', messageKey: 'used' },
};

export const MyWordsCounter: FC<MyWordsCounterProps> = ({
  type,
  count,
  occurrences,
  sessions,
  isLoading,
}) => {
  const intl = useIntl();
  const config = typeConfig[type];

  const getOccurrencesAndSessionsDisplay = useCallback(() => {
    const isHeardType = type === COUNTER_TYPES.HEARD;
    const part1Key = isHeardType
      ? msgs[MESSAGE_KEYS.HEARD_COUNT_PART1]
      : msgs[MESSAGE_KEYS.USED_COUNT_PART1];
    const part2Key = isHeardType
      ? msgs[MESSAGE_KEYS.HEARD_COUNT_PART2]
      : msgs[MESSAGE_KEYS.USED_COUNT_PART2];
    const part2Param = isHeardType ? { videosCount: sessions } : { convoCount: sessions };

    const part1 = intl.formatMessage(part1Key, { timesCount: occurrences });
    const part2 = intl.formatMessage(part2Key, part2Param);

    return `${part1} ${part2}`;
  }, [type, sessions, intl, occurrences]);

  const countDisplay = useMemo(() => {
    if (sessions === undefined) {
      return intl.formatMessage(msgs[MESSAGE_KEYS.WORDS_COUNT], { n: count });
    }

    if (type === COUNTER_TYPES.LEARNED) {
      return intl.formatMessage(msgs[MESSAGE_KEYS.LEARNED_COUNT], {
        n: count,
        testsCount: sessions,
      });
    }

    return getOccurrencesAndSessionsDisplay();
  }, [sessions, count, type, intl, getOccurrencesAndSessionsDisplay]);

  return (
    <S.Container $type={type} data-testid={`mywords_counter_${type}`}>
      <S.IconContainer>
        <S.StyledIcon name={config.icon} $type={type} />
      </S.IconContainer>
      <S.Content>
        <S.Title>{intl.formatMessage(msgs[config.messageKey])}</S.Title>
        {isLoading ? (
          <S.LoaderContainer>
            <Loader />
          </S.LoaderContainer>
        ) : (
          <S.Count>{countDisplay}</S.Count>
        )}
      </S.Content>
    </S.Container>
  );
};

export default MyWordsCounter;
